import { UIMarket } from '@drift/common';
import { produce } from 'immer';
import { AccountTabsValue } from 'src/components/Drawers/AccountsDrawer/AccountTabs';
import { AggregateLiqState } from 'src/hooks/Liquidations/useAggregateLiquidationDataForUser';
import { create } from 'zustand';
import { SpotMarketConfig } from '@drift-labs/sdk';
import { UiVaultConfig } from 'src/@types/vaults';
import { DRAWER_TRANSITION_DURATION } from 'src/components/Drawers/consts';

export type DrawerName = keyof Omit<
	DrawersStore,
	| 'handleCloseDrawer'
	| 'get'
	| 'set'
	| 'drawerIsTransitionClosing'
	| 'showDrawer'
>;

/**
 * DrawerStore assumes only 1 drawer can be open at a time.
 */
export interface DrawersStore {
	set: (x: (s: DrawersStore) => void) => void;
	get: (x: any) => DrawersStore;

	/**
	 * If true, the drawer is transitioning to close. This assumes only one drawer can be open at a time.
	 */
	drawerIsTransitionClosing: boolean;

	/**
	 * Handle closing the drawer and setting the state to defaults for that drawer.
	 */
	handleCloseDrawer: (panelKey: DrawerName) => void;

	/**
	 * Show the drawer and set the state to the default values for that drawer.
	 */
	showDrawer: (panelKey: DrawerName) => void;

	/**
	 * Drawers
	 */
	accounts: { show: boolean; defaultTab: AccountTabsValue };
	liquidations:
		| { show: true; liquidation: AggregateLiqState }
		| { show: false; liquidation: undefined };
	insuranceFundVault:
		| { show: true; marketIndex: number }
		| { show: false; marketIndex: undefined };
	marketDetails:
		| { show: true; uiMarket: UIMarket; goToPnl: boolean }
		| { show: false; uiMarket: undefined; goToPnl: false };
	vaultDetails:
		| { show: true; vault: UiVaultConfig }
		| { show: false; vault: undefined };
	borrowLendDetails: {
		displayTab: 'deposit' | 'borrow';
		showCloseBorrow?: boolean;
		targetAccountKey?: string;
	} & (
		| {
				show: true;
				market: SpotMarketConfig;
		  }
		| {
				show: false;
				market: undefined;
		  }
	);
	position: {
		show: boolean;
		marketIndex: number;
	};
	connectWallet: {
		show: boolean;
		completeWalletConnectCallback?: () => void;
	};
	marginLeverageSettings: {
		show: boolean;
	};
	listYourVault: {
		show: boolean;
	};
	vaultDisplayFilters: {
		show: boolean;
	};
	accountHotkeySelection: {
		show: boolean;
		selectedAccountIndex: number;
	};
}

export const useDrawersStore = create<DrawersStore>((set, get) => {
	const immerSet = (fn: (state: DrawersStore) => void) => set(produce(fn));

	return {
		set: immerSet,
		get: () => get(),
		drawerIsTransitionClosing: false,
		accounts: {
			show: false,
			defaultTab: 'accounts',
		},
		liquidations: {
			show: false,
			liquidation: undefined,
		},
		insuranceFundVault: {
			show: false,
			marketIndex: undefined,
		},
		marketDetails: {
			show: false,
			uiMarket: undefined,
			goToPnl: false as const,
		},
		betMarketDetails: {
			show: false,
			market: undefined,
		},
		vaultDetails: {
			show: false,
			vault: undefined,
		},
		borrowLendDetails: {
			show: false,
			market: undefined,
			displayTab: 'deposit',
		},
		position: {
			show: false,
			marketIndex: -1,
		},
		marginLeverageSettings: {
			show: false,
		},
		listYourVault: {
			show: false,
		},
		connectWallet: {
			show: false,
		},
		vaultDisplayFilters: {
			show: false,
		},
		accountHotkeySelection: {
			show: false,
			selectedAccountIndex: 0,
		},
		showDrawer: (panelKey: DrawerName) => {
			immerSet((state) => {
				state[panelKey].show = true;
			});
		},
		handleCloseDrawer: (panelKey: DrawerName) => {
			const state = get();

			if (state.drawerIsTransitionClosing || !state[panelKey].show) {
				return;
			}

			immerSet((state) => {
				state.drawerIsTransitionClosing = true;
			});

			const animationTimeout = setTimeout(() => {
				immerSet((state) => {
					state.drawerIsTransitionClosing = false;
					state[panelKey].show = false;

					// set props to default values (if relevant)
					if (panelKey === 'liquidations') {
						state[panelKey].liquidation = undefined;
					} else if (panelKey === 'accounts') {
						state.accounts.defaultTab = 'accounts';
					} else if (panelKey === 'borrowLendDetails') {
						state.borrowLendDetails.displayTab = 'deposit';
						state.borrowLendDetails.market = undefined;
					} else if (panelKey === 'position') {
						state.position.marketIndex = -1;
					}
				});
			}, DRAWER_TRANSITION_DURATION);

			return () => clearTimeout(animationTimeout);
		},
	};
});

export const useDrawerToggler = (drawerName: DrawerName) => {
	const set = useDrawersStore((s) => s.set);
	const hideDrawer = useDrawersStore((s) => s.handleCloseDrawer);

	const toggleDrawer = (show: boolean) => {
		if (show) {
			set((s) => {
				s[drawerName].show = true;
			});
		} else {
			hideDrawer(drawerName);
		}
	};

	return toggleDrawer;
};
